import { Fragment, useEffect, useMemo, useState } from 'react'
import { useLocation, Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { UserRound, AlertTriangle, ShoppingBag, Sparkles } from 'lucide-react'

import { SidebarTrigger } from '@/components/ui/sidebar'
import { PortalNotificationsMenu } from '@/components/layout/portal-notifications-menu'
import { EllanAiAssistanceDialog } from '@/components/ai/ellan-ai-assistance-dialog'
import { EditingLanguageSelector } from '@/components/layout/editing-language-selector'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { authApi } from '@/services/auth'
import { orgTreeApi, orgServiceInstanceLightQueryKey } from '@/services/org-tree'
import { useAuthStore } from '@/stores/auth-store'
import {
  getOrganizationTreeSetupLabel,
  getOrganizationTreeSetupLabelFromInstance,
  getServiceInstanceScopeLabel,
  getServiceInstanceScopeLabelFromInstance,
  buildServiceVariantHomePath,
  buildServiceVariantHomePathFromVariant,
  ORG_ORGANIZATION_PAGE_PATH,
  resolveActiveServiceInstanceId,
  resolveWorkspaceActiveServiceInstanceId,
  withServiceInstanceId,
} from '@/lib/service-instance'
import {
  isInAppChildServiceVariant,
  isServicesProductVariant,
  isTenantOrganizationServiceVariant,
} from '@/lib/service-variant-types'
import { useNodeLinkedServiceInstances } from '@/hooks/use-node-linked-service-instances'
import {
  buildNodeServicePermissionOptionsFromPosts,
  resolveNodeServicePermissionOption,
  type NodeServicePermissionOption,
} from '@/lib/node-role-services'
import { orgLevelVariantsApi } from '@/services/org-level-variants'
import { orgUsersApi } from '@/services/org-users'
import {
  commerceHeaderBasketCountQueryKey,
  fetchCommerceHeaderBasketItemCount,
} from '@/lib/commerce-header-basket-count'
import { evaluateRouteAccess } from '@/lib/route-access'
import { appendCommercePosDraftToHref } from '@/lib/commerce-pos-draft-storage'
import { getOrgUserGroupLabel, normalizeOrgUserGroupKey } from '@/lib/org-user-groups'
import { cn } from '@/lib/utils'

const routeTitles: Record<string, string> = {
  '/posts': 'Select Role',
  '/dashboard': 'Dashboard',
  '/help-support': 'Help & Support',
  '/job/dashboard': 'Jobs Dashboard',
  '/sales/dashboard': 'Sales Dashboard',
  '/services/bookings-dashboard': 'Bookings Dashboard',
  '/job': 'Jobs',
  '/job/recurring': 'Recurrence',
  '/job/live-jobs': 'Shifts',
  '/projects': 'Projects',
  '/job/customers': 'Customers',
  '/projects/customers': 'Customers',
  '/projects/assignments': 'Project Assignments',
  '/projects/workdays': 'Project Work Days',
  '/project/locations': 'Project Locations',
  '/projects/locations': 'Project Locations',
  '/job/create': 'Create Job',
  '/projects/create': 'Create Project',
  '/categories': 'Categories',
  '/tags': 'Tags',
  '/workers': 'Workers',
  '/job/workers': 'Workers',
  '/event/crew': 'Event Crew',
  '/events/crew': 'Event Crew',
  '/event/locations': 'Event Locations',
  '/events/locations': 'Event Locations',
  '/vehicles': 'Vehicles',
  '/org/users': 'Users',
  '/org/node': 'Node',
  '/org/node/child': 'Child Locations',
  '/org/node-details': 'Node Details',
  '/org/node/service-instrance': 'App Instances',
  '/org/service-instance': 'App Instance Directory',
  '/org/service-instrance/form': 'Organisation',
  '/org/service-instance/form': 'Organisation',
  '/org/node/branch-instrance': 'Branch Instances',
  '/org/node/service-instrance/config-form': 'App Instance Config',
  '/org/node/client-instrance/form': 'Store Form',
  '/org/node/client-add': 'Vendor Stores',
  '/org/node/subclient-add': 'Nested Vendor Stores',
  '/org/client/child': 'Vendor Branches',
  '/org/Client-branch-details': 'Vendor Branch Details',
  '/org/client-details': 'Vendor Details',
  '/org/organization': 'Organisation',
  '/org/branch': 'Branch',
  '/org/templates': 'Templates',
  '/org/tenants': 'Tenants',
  '/org/verticals': 'Verticals & levels',
  '/org/node/branch-instrance/media-form': 'Branch Media',
  '/job/locations': 'Locations',
  '/job/attendance': 'Attendance',
  '/job/attendance/live': 'Live',
  '/job/attendance/logs': 'Time Logs',
  '/job/attendance/clock': 'Clock Station',
  '/job/attendance/approvals': 'Approvals',
  '/live': 'Live',
  '/locations': 'Locations',
  '/workers/create': 'Add Worker',
  '/workers/approvals': 'Worker Approvals',
  '/workers/hotline-history': 'Hotline History',
  '/org/roles': 'Role Assignments',
  '/org/features': 'Features',
  '/settings/features': 'Features',
  '/org/manage-roles': 'Manage Roles',
  '/assignments': 'Assignments',
  '/assignments/create': 'Add worker',
  '/workdays': 'Work Days',
  '/timeline': 'Timeline',
  '/job/lifecycle': 'Job Lifecycle',
  '/projects/lifecycle': 'Project Lifecycle',
  '/projects/checklists': 'Project Checklists',
  '/projects/workflows': 'Project Workflows',
  '/projects/configuration': 'Project Configuration',
  '/finance/expenses': 'Expenses',
  '/job/expenses': 'Expenses',
  '/finance/particulars': 'Inventory',
  '/job/particulars': 'Inventory',
  '/job/worker/invoices': 'Worker Invoices',
  '/finance/worker-invoices': 'Worker Invoices',
  '/job/worker/payments': 'Worker Payouts',
  '/finance/worker-payments': 'Worker Payouts',
  '/job/company/payments': 'Payments to Company',
  '/finance/company-payments': 'Payments to Company',
  '/reports/worker': 'Worker Report',
  '/reports/company': 'Company Report',
  '/reports/client': 'Client Report',
  '/services/pos': 'New Booking',
  '/services/products': 'Services',
  '/services/product-variants': 'Packages',
  '/services/categories': 'Categories',
  '/services/quotations': 'Quotations',
  '/services/customers': 'Customers',
  '/services/orders': 'Bookings',
  '/services/credits': 'Credit',
  '/services/offers': 'Offers',
  '/membership/dashboard': 'Membership Dashboard',
  '/academic/dashboard': 'Academic Dashboard',
  '/academic/branches': 'Academic Branches',
  '/academic/courses': 'Academic Courses',
  '/academic/classes': 'Academic Classes',
  '/academic/subjects': 'Academic Subjects',
  '/academic/fees': 'Academic Fees',
  '/academic/people': 'Academic People',
  '/academic/tagsets': 'Academic Tag Sets',
  '/academic/tags': 'Academic Tags',
  '/academic/age-bands': 'Academic Age Bands',
  '/services/memberships': 'Memberships',
  '/services/memberships/subscriptions': 'Subscriptions',
  '/profile': 'Profile',
}

/** Titles for organisation settings form routes — aligned with `service-instance-form` page headings. */
function getOrganizationSettingsFormTitle(scopeLabel: string, search: string, settingsEntityLabel?: string) {
  const params = new URLSearchParams(search)
  const settingsMode = params.get('settingsMode')
  const action = params.get('action')
  const returnTo = params.get('returnTo')
  const lvlVariant = String(params.get('unique_level_variant_id') ?? params.get('lvl_variant') ?? '').trim()
  const resolvedSettingsEntityLabel = String(settingsEntityLabel ?? '').trim() || scopeLabel
  if (action === 'add' && returnTo === 'tenants') {
    const entityLabel = lvlVariant === 'head-tenant-lvl-variant' ? 'Organization' : 'Tenant'
    return `Add ${entityLabel}`
  }
  if (settingsMode === 'branch') {
    return 'Branch Configuration'
  }
  if (scopeLabel === 'Branch') {
    return 'Branch Settings'
  }
  return `${resolvedSettingsEntityLabel} Settings`
}

function getServiceInstanceDirectoryTitle(scopeLabel: string, settingsEntityLabel?: string) {
  const resolvedDirectoryEntityLabel = String(settingsEntityLabel ?? '').trim() || scopeLabel
  return `${resolvedDirectoryEntityLabel} Directory`
}

function getLevelVariantDisplayName(row?: {
  variant_name?: string | null
  variant_name_text?: string | null
  name?: string | null
} | null) {
  return String(
    row?.variant_name
    ?? row?.variant_name_text
    ?? row?.name
    ?? '',
  ).trim()
}

function fallbackUserGroupLabelFromParam(value: string): string {
  const normalized = normalizeOrgUserGroupKey(value)
  if (!normalized) {
    return 'Users'
  }

  return normalized
    .split('_')
    .filter((token) => token.length > 0)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ')
}

function getRouteTitle(
  pathname: string,
  scopeLabel: string,
  organizationSetupLabel: string,
  search = '',
  settingsEntityLabel = '',
  usersDirectoryTitle = 'Users',
) {
  const subscriptionDetailMatch = pathname.match(/^\/services\/memberships\/subscriptions\/([^/]+)$/)

  if (/^\/services\/memberships\/subscriptions\/[^/]+\/payments$/.test(pathname)) {
    return 'Invoice & Payments'
  }

  if (subscriptionDetailMatch) {
    return `Subscription #${subscriptionDetailMatch[1]}`
  }

  if (pathname === '/org' || pathname === '/org/tree' || pathname === '/org/organization') {
    return organizationSetupLabel
  }

  if (pathname === '/org/branch') {
    return 'My Branch'
  }

  if (pathname.startsWith('/org/node/service-instrance/media-form')) {
    return `${scopeLabel} Media`
  }

  if (pathname.startsWith('/org/node/service-instrance/config-form')) {
    return getOrganizationSettingsFormTitle(scopeLabel, search, settingsEntityLabel)
  }

  if (
    pathname.startsWith('/org/service-instrance/form')
    || pathname.startsWith('/org/service-instance/form')
    || pathname.startsWith('/organization/service-instrance/form')
    || pathname.startsWith('/organization/service-instance/form')
    || pathname.startsWith('/org/node/service-instrance/form')
    || pathname.startsWith('/organization/node/service-instrance/form')
    || pathname.startsWith('/organization/node/service-instance/form')
  ) {
    return getOrganizationSettingsFormTitle(scopeLabel, search, settingsEntityLabel)
  }

  if (
    pathname === '/org/service-instance'
    || pathname.startsWith('/org/service-instance/')
    || pathname === '/org/node/service-instrance'
    || pathname.startsWith('/org/node/service-instrance/')
    || pathname.startsWith('/org/node/branch-instrance/')
  ) {
    return getServiceInstanceDirectoryTitle(scopeLabel, settingsEntityLabel)
  }

  if (pathname === '/org/users') {
    return usersDirectoryTitle
  }

  if (pathname.startsWith('/org/users/')) {
    return `${usersDirectoryTitle} Details`
  }

  if (pathname.startsWith('/job/customers/') || pathname.startsWith('/services/customers/')) {
    return 'Customer Details'
  }

  if (routeTitles[pathname]) {
    return routeTitles[pathname]
  }

  if (/^\/assignments\/[^/]+\/edit$/.test(pathname)) {
    return 'Edit worker'
  }

  const match = Object.keys(routeTitles)
    .sort((left, right) => right.length - left.length)
    .find((route) => pathname.startsWith(`${route}/`))

  return match ? routeTitles[match] : 'Page'
}

function getBreadcrumbs(
  pathname: string,
  scopeLabel: string,
  organizationSetupLabel: string,
  search = '',
  settingsEntityLabel = '',
  usersDirectoryTitle = 'Users',
) {
  if (
    pathname.startsWith('/org/service-instrance/form/')
    || pathname.startsWith('/org/service-instance/form/')
    || pathname.startsWith('/organization/service-instrance/form/')
    || pathname.startsWith('/organization/service-instance/form/')
    || pathname.startsWith('/org/node/service-instrance/form/')
    || pathname.startsWith('/organization/node/service-instrance/form/')
    || pathname.startsWith('/organization/node/service-instance/form/')
  ) {
    const params = new URLSearchParams(search)
    const action = params.get('action')
    const returnTo = params.get('returnTo')
    const lvlVariant = String(params.get('unique_level_variant_id') ?? params.get('lvl_variant') ?? '').trim()
    if (action === 'add' && returnTo === 'tenants') {
      const entityLabel = lvlVariant === 'head-tenant-lvl-variant' ? 'Organization' : 'Tenant'
      const tenantsHref = lvlVariant
        ? `/org/tenants?unique_level_variant_id=${encodeURIComponent(lvlVariant)}&lvl_variant=${encodeURIComponent(lvlVariant)}`
        : '/org/tenants'
      const tenantsTitle = lvlVariant === 'head-tenant-lvl-variant' ? 'Organizations' : 'Tenants'
      return [
        { title: tenantsTitle, href: tenantsHref },
        { title: `Add ${entityLabel}`, href: pathname },
      ]
    }
    return [
      { title: organizationSetupLabel, href: ORG_ORGANIZATION_PAGE_PATH },
      { title: getRouteTitle(pathname, scopeLabel, organizationSetupLabel, search, settingsEntityLabel), href: pathname },
    ]
  }

  const parts = pathname.split('/').filter(Boolean)
  const breadcrumbs: { title: string; href: string }[] = []

  let currentPath = ''
  for (const part of parts) {
    currentPath += `/${part}`
    let title: string | undefined
    const subscriptionDetailMatch = currentPath.match(/^\/services\/memberships\/subscriptions\/([^/]+)$/)
    if (/^\/services\/memberships\/subscriptions\/[^/]+\/payments$/.test(currentPath)) {
      title = 'Invoice & Payments'
    } else if (subscriptionDetailMatch) {
      title = `Subscription #${subscriptionDetailMatch[1]}`
    } else if (currentPath === '/services/memberships/subscriptions') {
      title = 'Subscriptions'
    }

    if (!title && currentPath.startsWith('/org/node/service-instrance/media-form')) {
      title = `${scopeLabel} Media`
    } else if (!title && currentPath.startsWith('/org/node/service-instrance/config-form')) {
      title = getOrganizationSettingsFormTitle(scopeLabel, search, settingsEntityLabel)
    } else if (!title && (
      currentPath.startsWith('/org/service-instrance/form')
      || currentPath.startsWith('/org/service-instance/form')
      || currentPath.startsWith('/organization/service-instrance/form')
      || currentPath.startsWith('/organization/service-instance/form')
      || currentPath.startsWith('/org/node/service-instrance/form')
      || currentPath.startsWith('/organization/node/service-instrance/form')
      || currentPath.startsWith('/organization/node/service-instance/form')
    )) {
      title = getOrganizationSettingsFormTitle(scopeLabel, search, settingsEntityLabel)
    } else if (!title && (
      currentPath === '/org/service-instance'
      || currentPath.startsWith('/org/service-instance/')
      || currentPath === '/org/node/service-instrance'
      || currentPath.startsWith('/org/node/service-instrance/')
      || currentPath.startsWith('/org/node/branch-instrance/')
    )) {
      title = getServiceInstanceDirectoryTitle(scopeLabel, settingsEntityLabel)
    } else if (!title && (currentPath === '/org/tree' || currentPath === '/org/organization')) {
      title = organizationSetupLabel
    } else if (!title && currentPath === '/org') {
      title = 'Organization'
    } else if (!title && currentPath === '/org/branch') {
      title = 'My Branch'
    } else if (!title && currentPath === '/org/users') {
      title = usersDirectoryTitle
    } else if (!title && (currentPath.startsWith('/job/customers/') || currentPath.startsWith('/services/customers/'))) {
      title = 'Customer Details'
    } else if (!title) {
      title = routeTitles[currentPath]
    }

    if (!title) {
      const match = Object.keys(routeTitles)
        .sort((left, right) => right.length - left.length)
        .find((route) => currentPath.startsWith(`${route}/`))
      title = match ? routeTitles[match] : part.charAt(0).toUpperCase() + part.slice(1)
    }

    breadcrumbs.push({ title, href: currentPath })
  }

  return breadcrumbs
}

function formatHeaderDate(date: Date, timeZone?: string | null) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: timeZone || undefined,
    }).format(date)
  } catch {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(date)
  }
}

function formatHeaderTime(date: Date, timeZone?: string | null) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: timeZone || undefined,
    }).format(date)
  } catch {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }).format(date)
  }
}

function countryCodeToFlag(countryCode?: string | null) {
  const normalized = String(countryCode || '').trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(normalized)) {
    return ''
  }

  const [first, second] = normalized
  const firstCodePoint = first.charCodeAt(0) + 127397
  const secondCodePoint = second.charCodeAt(0) + 127397
  return String.fromCodePoint(firstCodePoint, secondCodePoint)
}

function isApprovedServiceStatus(status: unknown) {
  if (status == null) {
    return true
  }

  const normalized = String(status).trim().toLowerCase()
  return normalized === '1' || normalized === 'approved' || normalized === 'active'
}

function formatServiceStatusLabel(status: unknown) {
  const normalized = String(status ?? '').trim().toLowerCase()
  if (!normalized) {
    return 'Unknown'
  }

  if (normalized === '1' || normalized === 'approved' || normalized === 'active') return 'Approved'
  if (normalized === '0' || normalized === 'pending') return 'Pending'
  if (normalized === '2' || normalized === 'rejected') return 'Rejected'
  if (normalized === '3' || normalized === 'suspended') return 'Suspended'
  if (normalized === '4' || normalized === 'resigned' || normalized === 'terminated') return 'Terminated'

  return String(status)
}

function HeaderDateTimeBox({
  today,
  currentTime,
  countryFlag,
  countryLabel,
  tone,
  showFrom,
}: {
  today: string
  currentTime: string
  countryFlag: string
  countryLabel: string
  tone: 'solid' | 'glass'
  showFrom: 'sm' | 'lg'
}) {
  const hiddenUntil = showFrom === 'sm' ? 'hidden sm:block' : 'hidden lg:block'
  const shell =
    tone === 'glass'
      ? 'rounded-md border border-border bg-background/92 shadow-sm backdrop-blur'
      : 'rounded-md border border-border bg-background shadow-sm'

  return (
    <div
      data-testid="header-datetime"
      className={`${hiddenUntil} flex min-w-0 max-w-[22rem] shrink-0 items-center gap-3 px-2.5 py-1 leading-tight ${shell}`}
    >
      {countryLabel ? (
        <div className="min-w-0 flex-1 text-left">
          <p className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
            {countryFlag ? <span className="shrink-0">{countryFlag}</span> : null}
            <span className="truncate">{countryLabel}</span>
          </p>
        </div>
      ) : null}
      <div className="ml-auto flex shrink-0 flex-wrap items-baseline gap-x-1.5 text-right">
        <span className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
          {today}
        </span>
        <span className="select-none text-xs text-muted-foreground/60" aria-hidden>
          ·
        </span>
        <span className="text-xs font-semibold tabular-nums tracking-tight text-foreground">
          {currentTime}
        </span>
      </div>
    </div>
  )
}

function PosBasketNavButton({
  href,
  count,
  buttonClassName,
}: {
  href: string
  count: number
  buttonClassName: string
}) {
  const title = count > 0 ? `New Booking / basket (${count} items)` : 'New Booking / basket'
  const display = count > 99 ? '99+' : String(count)

  return (
    <Button variant="ghost" size="icon" className={cn('relative h-10 w-10', buttonClassName)} asChild>
      <Link to={href} title={title}>
        <ShoppingBag className="h-4 w-4" />
        <span className="sr-only">Open New Booking (POS)</span>
        {count > 0 ? (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground shadow-sm"
            aria-hidden
          >
            {display}
          </span>
        ) : null}
      </Link>
    </Button>
  )
}

function isPendingRegistrationStatus(status: unknown) {
  const normalized = String(status ?? '').trim().toLowerCase()
  return normalized === '1' || normalized === 'pending'
}

const IN_APP_BADGE_TONES: Record<string, string> = {
  '300': 'border-sky-300 bg-sky-100 text-sky-900 hover:bg-sky-200/80',
  '400': 'border-emerald-300 bg-emerald-100 text-emerald-900 hover:bg-emerald-200/80',
  '500': 'border-teal-300 bg-teal-100 text-teal-900 hover:bg-teal-200/80',
  '600': 'border-cyan-300 bg-cyan-100 text-cyan-900 hover:bg-cyan-200/80',
  '1200': 'border-lime-300 bg-lime-100 text-lime-900 hover:bg-lime-200/80',
  '1300': 'border-yellow-300 bg-yellow-100 text-yellow-900 hover:bg-yellow-200/80',
  '1500': 'border-stone-300 bg-stone-100 text-stone-900 hover:bg-stone-200/80',
  '1600': 'border-amber-300 bg-amber-100 text-amber-950 hover:bg-amber-200/80',
  '1700': 'border-orange-300 bg-orange-100 text-orange-950 hover:bg-orange-200/80',
  '1800': 'border-rose-300 bg-rose-100 text-rose-900 hover:bg-rose-200/80',
  '1900': 'border-fuchsia-300 bg-fuchsia-100 text-fuchsia-900 hover:bg-fuchsia-200/80',
  '2000': 'border-orange-300 bg-orange-100 text-orange-950 hover:bg-orange-200/80',
  '2100': 'border-red-300 bg-red-100 text-red-900 hover:bg-red-200/80',
  '2200': 'border-pink-300 bg-pink-100 text-pink-900 hover:bg-pink-200/80',
  '2300': 'border-indigo-300 bg-indigo-100 text-indigo-900 hover:bg-indigo-200/80',
  '2400': 'border-blue-300 bg-blue-100 text-blue-900 hover:bg-blue-200/80',
  '2500': 'border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200/80',
  '2600': 'border-zinc-300 bg-zinc-100 text-zinc-900 hover:bg-zinc-200/80',
  '2700': 'border-neutral-300 bg-neutral-100 text-neutral-900 hover:bg-neutral-200/80',
  // Commerce / Services store in-apps
  '700': 'border-red-300 bg-red-100 text-red-900 hover:bg-red-200/80',
  '800': 'border-green-300 bg-green-100 text-green-900 hover:bg-green-200/80',
  '1400': 'border-violet-300 bg-violet-100 text-violet-900 hover:bg-violet-200/80',
}

const IN_APP_BADGE_FALLBACK_TONES = [
  'border-sky-300 bg-sky-100 text-sky-900 hover:bg-sky-200/80',
  'border-emerald-300 bg-emerald-100 text-emerald-900 hover:bg-emerald-200/80',
  'border-amber-300 bg-amber-100 text-amber-950 hover:bg-amber-200/80',
  'border-rose-300 bg-rose-100 text-rose-900 hover:bg-rose-200/80',
  'border-teal-300 bg-teal-100 text-teal-900 hover:bg-teal-200/80',
  'border-orange-300 bg-orange-100 text-orange-950 hover:bg-orange-200/80',
]

function getInAppBadgeTone(variantId: string, variantLabel: string): string {
  const normalizedId = String(variantId ?? '').trim()
  if (normalizedId && IN_APP_BADGE_TONES[normalizedId]) {
    return IN_APP_BADGE_TONES[normalizedId]
  }

  const normalizedLabel = String(variantLabel ?? '').trim().toLowerCase()
  if (normalizedLabel.includes('membership')) return IN_APP_BADGE_TONES['400']
  if (normalizedLabel.includes('fundrais') || normalizedLabel.includes('charity')) return IN_APP_BADGE_TONES['1600']
  if (normalizedLabel.includes('event')) return IN_APP_BADGE_TONES['300']
  if (normalizedLabel.includes('temple') || normalizedLabel.includes('venue')) return IN_APP_BADGE_TONES['2000']
  if (normalizedLabel.includes('academic') || normalizedLabel.includes('acadamic')) return IN_APP_BADGE_TONES['500']
  if (normalizedLabel.includes('restaurant')) return IN_APP_BADGE_TONES['700']
  if (normalizedLabel.includes('grocery')) return IN_APP_BADGE_TONES['800']
  if (normalizedLabel.includes('service')) return IN_APP_BADGE_TONES['1400']

  const hashSource = normalizedId || normalizedLabel || 'in-app'
  let hash = 0
  for (let index = 0; index < hashSource.length; index += 1) {
    hash = ((hash << 5) - hash) + hashSource.charCodeAt(index)
    hash |= 0
  }
  return IN_APP_BADGE_FALLBACK_TONES[Math.abs(hash) % IN_APP_BADGE_FALLBACK_TONES.length]
}

export function AppHeader() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useAuthStore((state) => state.user)
  const isPendingUser = isPendingRegistrationStatus(user?.registration_status)
  const currentPost = useAuthStore((state) => state.currentPost)
  const availablePosts = useAuthStore((state) => state.availablePosts)
  const currentBranch = useAuthStore((state) => state.currentBranch)
  const availableBranches = useAuthStore((state) => state.availableBranches)
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const setServiceInstanceId = useAuthStore((state) => state.setServiceInstanceId)
  const accessToken = useAuthStore((state) => state.accessToken)
  const baseServiceInstanceId = resolveActiveServiceInstanceId({
    search: location.search,
    post: currentPost,
    currentBranch,
    availableBranches,
    storedServiceInstanceId,
    token: accessToken,
  })
  const headerWorkspaceNodeIdFromPost = String(currentPost?.node_id ?? '').trim()
  const isBranchWorkspaceRoute = location.pathname === '/org/branch'
  const postDerivedWorkspaceOptions = buildNodeServicePermissionOptionsFromPosts(
    availablePosts,
    headerWorkspaceNodeIdFromPost,
  )
  const nodeLinkedServiceInstances = useNodeLinkedServiceInstances({
    nodeId: headerWorkspaceNodeIdFromPost,
    activeServiceInstanceId: baseServiceInstanceId,
    enabled: Boolean(headerWorkspaceNodeIdFromPost),
    supplementalOptions: postDerivedWorkspaceOptions,
  })
  const serviceInstanceId = useMemo(
    () => resolveWorkspaceActiveServiceInstanceId({
      search: location.search,
      post: currentPost,
      currentBranch,
      availableBranches,
      storedServiceInstanceId,
      token: accessToken,
      nodeInstanceIds: nodeLinkedServiceInstances.serviceOptions.map((option) => option.instanceId),
    }) || baseServiceInstanceId,
    [
      availableBranches,
      baseServiceInstanceId,
      currentBranch,
      currentPost,
      location.search,
      nodeLinkedServiceInstances.serviceOptions,
      storedServiceInstanceId,
      accessToken,
    ],
  )
  const headerScopedServiceInstanceId = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const viewedId = String(params.get('id') ?? '').trim()
    const isSettingsRoute =
      location.pathname.startsWith('/org/service-instrance/form')
      || location.pathname.startsWith('/org/service-instance/form')
      || location.pathname.startsWith('/organization/service-instrance/form')
      || location.pathname.startsWith('/organization/service-instance/form')
      || location.pathname.startsWith('/org/node/service-instrance/config-form')

    if (isSettingsRoute && viewedId) {
      return viewedId
    }

    return serviceInstanceId
  }, [location.pathname, location.search, serviceInstanceId])
  const [currentDateTime, setCurrentDateTime] = useState(() => new Date())
  const [aiAssistOpen, setAiAssistOpen] = useState(false)

  const serviceInstanceQuery = useQuery({
    queryKey: orgServiceInstanceLightQueryKey(String(headerScopedServiceInstanceId)),
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(String(headerScopedServiceInstanceId)),
    enabled: Boolean(headerScopedServiceInstanceId) && !isBranchWorkspaceRoute,
    staleTime: 5 * 60 * 1000,
  })
  const selectedLevelVariantId = useMemo(
    () => {
      const params = new URLSearchParams(location.search)
      const legacy = String(params.get('unique_level_variant_id') ?? '').trim()
      if (legacy) {
        return legacy
      }
      const generic = String(params.get('lvl_variant') ?? '').trim()
      if (!generic) {
        return ''
      }
      const alias = generic.toLowerCase()
      if (alias === 'branch') return 'branch-lvl-variant'
      if (alias === 'tenant') return 'tenant-lvl-variant'
      if (alias === 'root' || alias === 'org' || alias === 'organization') return 'root-org-lvl-variant'
      return generic
    },
    [location.search],
  )
  const selectedLevelVariantQuery = useQuery({
    queryKey: ['header-level-variant', serviceInstanceId, selectedLevelVariantId],
    queryFn: async () => {
      const rows = await orgLevelVariantsApi.list(String(serviceInstanceId))
      return rows.find((row) => String(row.unique_id ?? '').trim() === selectedLevelVariantId) ?? null
    },
    enabled: Boolean(serviceInstanceId) && Boolean(selectedLevelVariantId),
    staleTime: 5 * 60 * 1000,
  })

  const activeUserGroupFilter = useMemo(
    () => String(new URLSearchParams(location.search).get('user_group') ?? '').trim(),
    [location.search],
  )
  const activeUserGroupLabelFilter = useMemo(
    () => normalizeOrgUserGroupKey(new URLSearchParams(location.search).get('user_group_label')),
    [location.search],
  )

  const userGroupsQuery = useQuery({
    queryKey: ['header-org-user-groups', serviceInstanceId],
    queryFn: () => orgUsersApi.listGroups({ serviceInstanceId }),
    enabled: Boolean(serviceInstanceId) && location.pathname.startsWith('/org/users'),
    staleTime: 60_000,
  })

  const scopeLabel = useMemo(() => {
    const instance = serviceInstanceQuery.data
    if (instance) {
      return getServiceInstanceScopeLabelFromInstance(instance)
    }
    return getServiceInstanceScopeLabel(
      currentPost?.service_variant_unique_id,
      currentPost?.service_variant_name ?? currentPost?.service_variant_type,
    )
  }, [
    serviceInstanceQuery.data,
    currentPost?.service_variant_name,
    currentPost?.service_variant_type,
    currentPost?.service_variant_unique_id,
  ])

  const organizationSetupLabel = useMemo(() => {
    const instance = serviceInstanceQuery.data
    if (instance) {
      return getOrganizationTreeSetupLabelFromInstance(instance)
    }
    return getOrganizationTreeSetupLabel(
      currentPost?.service_variant_unique_id,
      currentPost?.service_variant_name ?? currentPost?.service_variant_type,
    )
  }, [
    serviceInstanceQuery.data,
    currentPost?.service_variant_name,
    currentPost?.service_variant_type,
    currentPost?.service_variant_unique_id,
  ])
  const selectedHeaderWorkspaceOption = resolveNodeServicePermissionOption(
    nodeLinkedServiceInstances.serviceOptions,
    serviceInstanceId,
  )
  const headerHomeHref = selectedHeaderWorkspaceOption
    ? buildServiceVariantHomePathFromVariant(
      selectedHeaderWorkspaceOption.variantId,
      selectedHeaderWorkspaceOption.variantLabel,
      serviceInstanceId,
    )
    : buildServiceVariantHomePath(currentPost, serviceInstanceId)

  // Hide in-app badges only when the active role itself is a TENANT service
  // variant. Branch roles must retain their app switcher even when the branch
  // belongs to a tenant or the current route is under tenant administration.
  const hideHeaderInAppsList = isTenantOrganizationServiceVariant(
    currentPost?.service_variant_unique_id,
    currentPost?.service_variant_name ?? currentPost?.service_variant_type,
  )

  const headerInAppOptions = useMemo(
    () => {
      if (hideHeaderInAppsList) {
        return []
      }
      return nodeLinkedServiceInstances.serviceOptions.filter((option) => (
        isInAppChildServiceVariant(option.variantId, option.variantLabel, {
          levelVariantUniqueId: option.levelVariantUniqueId,
          levelVariantName: option.levelVariantName,
          hierarchyTierCode: option.hierarchyTierCode,
        })
      ))
    },
    [hideHeaderInAppsList, nodeLinkedServiceInstances.serviceOptions],
  )

  const handleHeaderInAppSelect = (option: NodeServicePermissionOption) => {
    if (!option.instanceId || option.instanceId === serviceInstanceId) {
      return
    }

    setServiceInstanceId(option.instanceId)
    queryClient.invalidateQueries({ queryKey: ['sidebar-active-service-instance'] })
    // Do not invalidate sidebar-policy-features: SI is already in the query key,
    // so the destination SI fetches once. Broad invalidation refetched old+new
    // observers (and Events dual role keys) and slowed in-app switches.
    navigate(
      buildServiceVariantHomePathFromVariant(
        option.variantId,
        option.variantLabel,
        option.instanceId,
      ),
      { replace: true },
    )
  }

  const settingsEntityLabel = useMemo(() => {
    const selectedLevelVariantName = getLevelVariantDisplayName(selectedLevelVariantQuery.data)
    if (selectedLevelVariantName) {
      return selectedLevelVariantName
    }

    const levelVariantName = String(serviceInstanceQuery.data?.level_variant_name ?? '').trim()
    if (levelVariantName) {
      return levelVariantName
    }
    return scopeLabel
  }, [scopeLabel, selectedLevelVariantQuery.data, serviceInstanceQuery.data?.level_variant_name])

  const usersDirectoryTitle = useMemo(() => {
    if (!location.pathname.startsWith('/org/users')) {
      return 'Users'
    }

    const groups = userGroupsQuery.data ?? []
    if (activeUserGroupFilter && activeUserGroupFilter.toLowerCase() !== 'all') {
      const byId = groups.find((group) => String(group.id) === activeUserGroupFilter)
      if (byId) {
        return getOrgUserGroupLabel(byId.title, 'Users')
      }
    }

    if (activeUserGroupLabelFilter) {
      const byLabel = groups.find((group) => {
        const normalizedTitle = normalizeOrgUserGroupKey(group.title)
        const normalizedCode = normalizeOrgUserGroupKey(group.unique_code)
        return normalizedTitle === activeUserGroupLabelFilter || normalizedCode === activeUserGroupLabelFilter
      })
      if (byLabel) {
        return getOrgUserGroupLabel(byLabel.title, fallbackUserGroupLabelFromParam(activeUserGroupLabelFilter))
      }

      return fallbackUserGroupLabelFromParam(activeUserGroupLabelFilter)
    }

    return 'Users'
  }, [activeUserGroupFilter, activeUserGroupLabelFilter, location.pathname, userGroupsQuery.data])

  const breadcrumbs = getBreadcrumbs(
    location.pathname,
    scopeLabel,
    organizationSetupLabel,
    location.search,
    settingsEntityLabel,
    usersDirectoryTitle,
  )
  const currentTitle = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].title : 'Dashboard'

  const countriesQuery = useQuery({
    queryKey: ['header-country-list'],
    queryFn: () => orgTreeApi.listCountries(),
    enabled: !isBranchWorkspaceRoute,
    staleTime: 30 * 60 * 1000,
  })

  const timezonesQuery = useQuery({
    queryKey: ['header-timezone-list'],
    queryFn: () => orgTreeApi.listTimezones(),
    enabled: !isBranchWorkspaceRoute,
    staleTime: 30 * 60 * 1000,
  })

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentDateTime(new Date())
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [])

  const serviceTimeZoneReference = serviceInstanceQuery.data?.time_zone_id ?? serviceInstanceQuery.data?.time_zone
  const activeTimezone = serviceTimeZoneReference != null
    ? (timezonesQuery.data || []).find((timezone) => {
        if (String(timezone.id) === String(serviceTimeZoneReference)) {
          return true
        }

        return String(timezone.timezone || '').trim() === String(serviceTimeZoneReference).trim()
      })
    : null
  const resolvedTimeZone = activeTimezone?.timezone
    || (typeof serviceTimeZoneReference === 'string' && serviceTimeZoneReference.includes('/')
      ? serviceTimeZoneReference
      : null)

  const today = formatHeaderDate(currentDateTime, resolvedTimeZone)
  const currentTime = formatHeaderTime(currentDateTime, resolvedTimeZone)
  const countryId = serviceInstanceQuery.data?.country_id != null
    ? String(serviceInstanceQuery.data.country_id)
    : ''
  const activeCountry = countryId
    ? (countriesQuery.data || []).find((country) => String(country.id) === countryId)
    : null
  const countryFlag = activeCountry?.flag || countryCodeToFlag(activeCountry?.country_code)
  const countryLabel = activeCountry?.country_name || ''
  const serviceStatus = serviceInstanceQuery.data?.status
  const showNonApprovedWarning = !isApprovedServiceStatus(serviceStatus)
  const serviceStatusLabel = formatServiceStatusLabel(serviceStatus)

  const isPostGate = location.pathname === '/posts'
  const isServiceStoreWorkspace = isServicesProductVariant(
    currentPost?.service_variant_unique_id,
    currentPost?.service_variant_name ?? currentPost?.service_variant_type,
  )
  // Basket / New Booking is Service Store (1400) only — never venue, fundraising, etc.
  const showPosBasketShortcut = isServiceStoreWorkspace
    && evaluateRouteAccess('/services/pos', currentPost).authorized
  const posBasketHref = appendCommercePosDraftToHref(
    withServiceInstanceId('/services/pos', serviceInstanceId),
    serviceInstanceId,
  )

  const commerceBasketCountQuery = useQuery({
    queryKey: commerceHeaderBasketCountQueryKey(serviceInstanceId),
    queryFn: () => fetchCommerceHeaderBasketItemCount(serviceInstanceId),
    enabled: showPosBasketShortcut && Boolean(serviceInstanceId),
    staleTime: 4_000,
    placeholderData: (previous) => previous,
  })
  const commerceBasketBadgeCount = commerceBasketCountQuery.data ?? 0

  const handleLogout = async () => {
  try {
    const authStorage = localStorage.getItem('auth-storage')

    let tenantServiceInstanceId = ''

    if (authStorage) {
      const parsed = JSON.parse(authStorage)
      const state = parsed?.state ?? parsed

      // Always get TENANT post
      const tenantPost = state?.availablePosts?.find(
        (post: any) =>
          post?.level_variant_unique_id === 'head-tenant-lvl-variant' ||
          post?.service_variant_type === 'TENANT' ||
          post?.service_variant_name === 'TENANT'
      )

      tenantServiceInstanceId =
        tenantPost?.service_instance_id ?? ''
    }

    await authApi.logout()

    // clear only after getting tenant id
    localStorage.clear()

    window.location.href = tenantServiceInstanceId
      ? `/login?service_instance_id=${encodeURIComponent(
          tenantServiceInstanceId
        )}`
      : '/login'
  } catch (error) {
    console.error('Logout failed:', error)

    localStorage.clear()
    window.location.href = '/login'
  }
}

  if (isPostGate) {
    return (
      <>
        <header className="sticky top-0 z-20 mx-4 mt-4 flex shrink-0 items-center rounded-2xl bg-transparent px-0 py-0 xl:mx-8">
          <div className="ml-auto flex items-center gap-2">
            <HeaderDateTimeBox
              today={today}
              currentTime={currentTime}
              countryFlag={countryFlag}
              countryLabel={countryLabel}
              tone="glass"
              showFrom="sm"
            />

            {showPosBasketShortcut ? (
              <PosBasketNavButton
                href={posBasketHref}
                count={commerceBasketBadgeCount}
                buttonClassName="rounded-lg border border-border bg-background/92 backdrop-blur"
              />
            ) : null}

            <EditingLanguageSelector triggerButtonClassName="bg-background/92 backdrop-blur" />

            <PortalNotificationsMenu
              serviceInstanceId={serviceInstanceId}
              triggerButtonClassName="rounded-lg border border-border bg-background/92 backdrop-blur"
            />

            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-lg border border-border bg-background/92 backdrop-blur"
              onClick={() => setAiAssistOpen(true)}
              title="Ellan AI Assistance"
            >
              <Sparkles className="h-4 w-4" />
              <span className="sr-only">Open Ellan AI Assistance</span>
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10 rounded-lg border border-border bg-background/92 backdrop-blur">
                  <UserRound className="h-4 w-4" />
                  <span className="sr-only">Profile</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[200px]">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-xs text-muted-foreground">My Account</span>
                    <span className="font-medium text-foreground">{user ? `${user.first_name} ${user.last_name}`.trim() || 'User' : 'User'}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {!isPendingUser ? (
                  <DropdownMenuItem asChild>
                    <Link to={withServiceInstanceId('/profile', serviceInstanceId)}>Profile</Link>
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem onClick={() => void handleLogout()}>
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        {showNonApprovedWarning ? (
          <div className="sticky top-[72px] z-10 mx-4 mt-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2 text-amber-900 shadow-sm xl:mx-8">
            <p className="flex items-center gap-2 text-sm font-medium">
              <AlertTriangle className="h-4 w-4" />
              Selected {scopeLabel} status is {serviceStatusLabel}. Access is restricted.
            </p>
          </div>
        ) : null}
      </>
    )
  }

  return (
    <>
      <header className="sticky top-0 z-20 mx-4 mt-4 flex shrink-0 items-center gap-3 rounded-2xl border border-border/80 bg-background/92 px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_12px_24px_-22px_rgba(15,23,42,0.2)] backdrop-blur xl:mx-8">
        <div className="flex items-center gap-3">
        <SidebarTrigger className="h-9 w-9 rounded-lg border border-border bg-background shadow-sm" />
        <Separator orientation="vertical" className="hidden h-9 md:block" />
        <Link
          to={headerHomeHref}
          aria-label="Go to dashboard from header"
          className="min-w-0 rounded-lg px-1 py-1 transition-colors hover:bg-accent/40"
        >
          <div className="flex items-center gap-2">
            <h1 className="truncate text-base font-semibold text-foreground sm:text-lg">{currentTitle}</h1>
          </div>
        </Link>
        </div>

        {headerInAppOptions.length > 0 ? (
          <nav
            aria-label="In-apps"
            className="flex min-w-0 max-w-[52%] items-center gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {headerInAppOptions.map((option) => {
              const isActive = option.instanceId === serviceInstanceId
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => handleHeaderInAppSelect(option)}
                  title={option.instanceName || option.variantLabel}
                  aria-current={isActive ? 'page' : undefined}
                  className="shrink-0"
                >
                  <Badge
                    variant="outline"
                    className={cn(
                      'cursor-pointer px-2.5 py-1 text-[11px] font-semibold tracking-wide transition-shadow',
                      getInAppBadgeTone(option.variantId, option.variantLabel),
                      isActive && 'ring-2 ring-foreground/25 ring-offset-1 ring-offset-background',
                    )}
                  >
                    {option.variantLabel}
                  </Badge>
                </button>
              )
            })}
          </nav>
        ) : null}

        <div className="hidden min-w-0 flex-1 xl:block">
        <Breadcrumb>
          <BreadcrumbList>
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1

              return (
                <Fragment key={crumb.href}>
                  <BreadcrumbItem>
                    {isLast ? (
                      <BreadcrumbPage>{crumb.title}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={withServiceInstanceId(crumb.href, serviceInstanceId)}>{crumb.title}</Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                  {!isLast ? <BreadcrumbSeparator /> : null}
                </Fragment>
              )
            })}
          </BreadcrumbList>
        </Breadcrumb>
        </div>

        <div className="ml-auto flex items-center gap-2">
        <HeaderDateTimeBox
          today={today}
          currentTime={currentTime}
          countryFlag={countryFlag}
          countryLabel={countryLabel}
          tone="solid"
          showFrom="lg"
        />
        {showPosBasketShortcut ? (
          <PosBasketNavButton
            href={posBasketHref}
            count={commerceBasketBadgeCount}
            buttonClassName="shrink-0 rounded-lg border border-border bg-background"
          />
        ) : null}

        <EditingLanguageSelector />

        <PortalNotificationsMenu serviceInstanceId={serviceInstanceId} />

        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 rounded-lg border border-border bg-background"
          onClick={() => setAiAssistOpen(true)}
          title="Ellan AI Assistance"
        >
          <Sparkles className="h-4 w-4" />
          <span className="sr-only">Open Ellan AI Assistance</span>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-10 w-10 rounded-lg border border-border bg-background">
              <UserRound className="h-4 w-4" />
              <span className="sr-only">Profile</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[200px]">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground">My Account</span>
                <span className="font-medium text-foreground">{user ? `${user.first_name} ${user.last_name}`.trim() || 'User' : 'User'}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {!isPendingUser ? (
              <DropdownMenuItem asChild>
                <Link to={withServiceInstanceId('/profile', serviceInstanceId)}>Profile</Link>
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem onClick={() => void handleLogout()}>
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </header>
      {showNonApprovedWarning ? (
        <div className="sticky top-[72px] z-10 mx-4 mt-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2 text-amber-900 shadow-sm xl:mx-8">
          <p className="flex items-center gap-2 text-sm font-medium">
            <AlertTriangle className="h-4 w-4" />
            Selected {scopeLabel} status is {serviceStatusLabel}. Access is restricted.
          </p>
        </div>
      ) : null}

      <EllanAiAssistanceDialog
        open={aiAssistOpen}
        onOpenChange={setAiAssistOpen}
        serviceInstanceId={serviceInstanceId}
      />
    </>
  )
}
